<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Email members"
    :style="{ width: '36rem' }"
    :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
    :closable="!sending"
    @hide="onHide"
  >
    <div class="kb-form">
      <p class="kb-dialog-lead">
        Sends from <strong>{{ subkrewe.name }} (Chewbacchus)</strong>, with replies going to
        <strong>{{ captainEmail || 'your account email' }}</strong>. Members never see each other's addresses.
      </p>

      <!--
        Audience. `scroll-height` is set because PrimeVue's 14rem default fits
        about five options and this list has six — hiding exactly one behind a
        scrollbar most people never notice. The min() keeps it from overrunning
        a phone viewport.
      -->
      <div class="kb-field">
        <label
          id="subkrewe-email-audience-label"
          for="subkrewe-email-audience"
        >Send to</label>
        <Select
          id="subkrewe-email-audience"
          v-model="audience"
          aria-labelledby="subkrewe-email-audience-label"
          :options="audienceOptions"
          option-label="label"
          option-value="value"
          scroll-height="min(20rem, 60vh)"
          size="small"
          fluid
          :disabled="sending"
        />
        <small
          v-if="effectiveGrant === 'redshirt_admin'"
          class="kb-field-hint"
        >
          As a Red Shirt Admin you can email the Red Shirts in this SubKrewe. Its other members
          are reachable by their own captains.
        </small>
        <small
          v-if="skippedCount > 0"
          class="kb-field-hint !text-kb-gold"
        >
          {{ skippedCount }} {{ skippedCount === 1 ? 'member has an address that is' : 'members have addresses that are' }}
          rejecting mail, so {{ skippedCount === 1 ? 'they' : 'they' }} won't receive this. Look for the
          warning icon in your member list.
        </small>
        <!-- Relay heads-up -->
        <small
          v-if="relayCount > 0"
          class="kb-field-hint"
        >
          {{ relayCount }} of these {{ relayCount === 1 ? 'members uses' : 'members use' }} Apple's Hide My Email.
          They'll receive this, but if they reply, replying to them from a personal address will bounce &mdash;
          come back here instead.
        </small>
      </div>

      <div
        v-if="audience === 'custom'"
        class="kb-field"
      >
        <label
          id="subkrewe-email-recipients-label"
          for="subkrewe-email-recipients"
        >Recipients</label>
        <MultiSelect
          id="subkrewe-email-recipients"
          v-model="selectedMemberIds"
          aria-labelledby="subkrewe-email-recipients-label"
          :options="selectableMembers"
          option-label="label"
          option-value="id"
          filter
          display="chip"
          placeholder="Pick members"
          size="small"
          fluid
          :disabled="sending"
        />
      </div>

      <!-- Subject -->
      <div class="kb-field">
        <label for="subkrewe-email-subject">Subject</label>
        <InputText
          id="subkrewe-email-subject"
          v-model="subject"
          size="small"
          fluid
          autofocus
          :maxlength="limits.subjectMax"
          :disabled="sending"
          placeholder="Build day this Saturday"
        />
      </div>

      <!-- Body -->
      <div class="kb-field">
        <label for="subkrewe-email-body">Message</label>
        <Textarea
          id="subkrewe-email-body"
          v-model="body"
          rows="10"
          auto-resize
          size="small"
          fluid
          :maxlength="limits.bodyMax"
          :disabled="sending"
          aria-describedby="subkrewe-email-body-count"
          placeholder="Write your message. Blank lines start a new paragraph, and links are turned into links automatically."
        />
        <small
          id="subkrewe-email-body-count"
          class="kb-field-hint"
        >
          {{ body.length }} / {{ limits.bodyMax }}
        </small>
      </div>

      <!-- Attachments -->
      <div class="kb-field">
        <div class="flex items-center justify-between gap-2">
          <span class="text-[0.8125rem] font-semibold text-kb-fg">
            Attachments <span class="kb-field-optional">(optional)</span>
          </span>
          <Button
            label="Add files"
            icon="pi pi-paperclip"
            size="small"
            text
            severity="secondary"
            :disabled="sending || attachmentFiles.length >= limits.maxAttachments"
            @click="fileInput?.click()"
          />
        </div>
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          :accept="acceptAttribute"
          @change="onFilesPicked"
        />

        <ul
          v-if="attachmentFiles.length"
          class="kb-attachments"
        >
          <li
            v-for="file in attachmentFiles"
            :key="file.storagePath"
            class="kb-attachment"
          >
            <i class="pi pi-file text-kb-dim !text-xs" />
            <span class="flex-grow truncate text-kb-fg">{{ file.filename }}</span>
            <span class="kb-row-meta shrink-0">{{ formatBytes(file.size) }}</span>
            <KbSpinner
              v-if="file.uploading"
              label="Uploading"
            />
            <i
              v-else-if="file.error"
              v-tooltip.left="file.error"
              class="pi pi-exclamation-triangle text-kb-alert !text-xs"
              role="img"
              :aria-label="file.error"
            />
            <Button
              icon="pi pi-times"
              text
              rounded
              size="small"
              severity="secondary"
              class="kb-attachment-remove"
              :disabled="sending"
              :aria-label="`Remove ${file.filename}`"
              @click="removeFile(file)"
            />
          </li>
        </ul>

        <small class="kb-field-hint">
          Up to {{ limits.maxAttachments }} files, {{ formatBytes(limits.maxTotalAttachmentBytes) }} total.
          PDFs, images, and Office documents.
        </small>
      </div>

      <!-- Problems -->
      <div
        v-if="problems.length"
        class="grid gap-1"
      >
        <small
          v-for="problem in problems"
          :key="problem"
          class="kb-field-error"
        >
          {{ problem }}
        </small>
      </div>
    </div>

    <template #footer>
      <Button
        label="Cancel"
        text
        severity="secondary"
        :disabled="sending"
        @click="isVisible = false"
      />
      <Button
        :label="sending ? 'Sending…' : `Send to ${recipientCount} ${recipientCount === 1 ? 'recipient' : 'recipients'}`"
        :loading="sending"
        :disabled="!canSend"
        @click="send"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Member } from '~/shared/types/member.js'
import type { SubKrewe } from '~/shared/types/subkrewe.js'
import type { SubkreweEmailAudience } from '~/shared/types/subkreweEmail.js'
import type { SubkreweEmailGrant } from '~/shared/utils/subkreweEmail.js'
import { memberShouldPayDues } from '~/composables/useShouldPayDues.js'
import { isEmailUndeliverable } from '~/shared/utils/emailHealth.js'
import {
  allowedAudiencesForGrant,
  ALLOWED_ATTACHMENT_TYPES,
  candidatesForGrant,
  formatBytes,
  isAppleRelayAddress,
  selectSubkreweEmailRecipients,
  SUBKREWE_EMAIL_AUDIENCE_LABELS,
  SUBKREWE_EMAIL_LIMITS,
  validateSubkreweEmailInput,
} from '~/shared/utils/subkreweEmail.js'

interface Props {
  visible: boolean
  subkrewe: SubKrewe
  /** Where replies land. Passed in so this component's setup stays sync. */
  captainEmail?: string
  /**
   * How the viewer earned the right to send. Narrows the audience list — a
   * Red Shirt Admin reaches Red Shirts only. The server enforces the same
   * rule; this just keeps the UI from offering something it would reject.
   */
  grant?: SubkreweEmailGrant
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:visible': [value: boolean]
  'sent': [payload: { recipientCount: number }]
}>()

const toast = useToast()
const subkrewesStore = useSubKrewesStore()
const {
  draftId,
  files: attachmentFiles,
  errors: attachmentErrors,
  uploading: attachmentsUploading,
  ready: readyAttachments,
  addFiles,
  removeFile,
  reset: resetAttachments,
} = useSubkreweEmailAttachments()

const limits = SUBKREWE_EMAIL_LIMITS
const acceptAttribute = ALLOWED_ATTACHMENT_TYPES.join(',')

const isVisible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value),
})

const effectiveGrant = computed<SubkreweEmailGrant>(() => props.grant ?? 'captain')
const allowedAudiences = computed(() => allowedAudiencesForGrant(effectiveGrant.value))

const subject = ref('')
const body = ref('')
const audience = ref<SubkreweEmailAudience>(allowedAudiences.value[0] ?? 'all')
const selectedMemberIds = ref<string[]>([])
const sending = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

/**
 * Mirrors the server's candidate shape so the count shown on the button is
 * produced by the same selector the endpoint uses. The server still recomputes
 * everything against a fresh read at send time — this is only a preview.
 */
const candidates = computed(() => {
  const members = (props.subkrewe.members ?? []) as Member[]
  const mapped = members.map(member => ({
    id: member.id,
    name: member.name ?? '',
    email: member.email ?? '',
    member_type: member.member_type,
    shouldPayDues: memberShouldPayDues(member),
    undeliverable: isEmailUndeliverable(member),
  }))
  return candidatesForGrant(mapped, effectiveGrant.value)
})

const audienceOptions = computed(() =>
  allowedAudiences.value.map(value => ({
    value,
    label: value === 'custom'
      ? SUBKREWE_EMAIL_AUDIENCE_LABELS[value]
      : `${SUBKREWE_EMAIL_AUDIENCE_LABELS[value]} (${selectSubkreweEmailRecipients(candidates.value, value).length})`,
  })),
)

const selectableMembers = computed(() =>
  candidates.value
    .filter(candidate => candidate.email)
    .map(candidate => ({ id: candidate.id, label: `${candidate.name || 'Unnamed'} — ${candidate.email}` }))
    .sort((a, b) => a.label.localeCompare(b.label)),
)

const resolvedRecipients = computed(() =>
  selectSubkreweEmailRecipients(candidates.value, audience.value, selectedMemberIds.value),
)
const recipientCount = computed(() => resolvedRecipients.value.length)

/**
 * Members the audience would have included but whose address SES has told us
 * is dead. Shown so a captain knows the gap exists rather than assuming
 * everyone was reached.
 */
const skippedCount = computed(() => {
  const members = (props.subkrewe.members ?? []) as Member[]
  return members.filter(member => isEmailUndeliverable(member)).length
})
const relayCount = computed(() => resolvedRecipients.value.filter(r => isAppleRelayAddress(r.email)).length)

const problems = computed(() => {
  if (!subject.value && !body.value) return []
  return validateSubkreweEmailInput({
    subject: subject.value,
    body: body.value,
    audience: audience.value,
    memberIds: selectedMemberIds.value,
    attachments: attachmentFiles.value,
  })
})

const canSend = computed(() =>
  !sending.value
  && !attachmentsUploading.value
  && recipientCount.value > 0
  && Boolean(subject.value.trim())
  && Boolean(body.value.trim())
  && validateSubkreweEmailInput({
    subject: subject.value,
    body: body.value,
    audience: audience.value,
    memberIds: selectedMemberIds.value,
    attachments: attachmentFiles.value,
  }).length === 0,
)

async function onFilesPicked(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const picked = Array.from(input.files ?? [])
  input.value = ''
  if (picked.length) await addFiles(picked)

  for (const problem of attachmentErrors.value) {
    toast.add({ severity: 'warn', summary: 'Attachment', detail: problem, life: 6000 })
  }
}

async function send(): Promise<void> {
  if (!canSend.value) return
  sending.value = true

  try {
    const result = await $fetch<{ recipientCount: number, relayCount: number, redirectedTo: string | null }>(
      '/api/subkrewes/send-email',
      {
        method: 'POST',
        body: {
          subkreweId: props.subkrewe.id,
          draftId: draftId.value,
          subject: subject.value,
          body: body.value,
          audience: audience.value,
          memberIds: selectedMemberIds.value,
          attachments: readyAttachments.value.map(file => ({
            storagePath: file.storagePath,
            filename: file.filename,
          })),
        },
      },
    )

    toast.add({
      severity: 'success',
      summary: 'Email queued',
      detail: result.redirectedTo
        ? `Redirected to ${result.redirectedTo} (development mode).`
        : `Sending to ${result.recipientCount} ${result.recipientCount === 1 ? 'member' : 'members'}.`,
      life: 6000,
    })

    emit('sent', { recipientCount: result.recipientCount })
    resetForm()
    isVisible.value = false
  }
  catch (error) {
    const message = error instanceof Error ? error.message : 'Could not send the email'
    const detail = (error as { data?: { message?: string } })?.data?.message ?? message
    toast.add({ severity: 'error', summary: 'Send failed', detail, life: 8000 })
  }
  finally {
    sending.value = false
  }
}

function resetForm(): void {
  subject.value = ''
  body.value = ''
  audience.value = allowedAudiences.value[0] ?? 'all'
  selectedMemberIds.value = []
  resetAttachments()
}

function onHide(): void {
  if (!sending.value) resetAttachments()
}

// A fresh draft each time the dialog opens keeps attachment paths from one
// aborted compose out of the next one.
watch(() => props.visible, (visible) => {
  if (!visible) return
  audience.value = allowedAudiences.value[0] ?? 'all'
  resetAttachments()

  // The admin drawer can open this before anything has populated
  // `subkrewe.members`, which would show a count of zero. Recipient
  // resolution is the server's job either way; this just keeps the preview
  // honest.
  if (!props.subkrewe.members?.length) {
    subkrewesStore.loadSubkreweMembers(props.subkrewe).catch(() => {
      // Non-fatal: the send endpoint reads the membership itself.
    })
  }
})
</script>
